import { motion } from "framer-motion";
import { Fingerprint, ReceiptText, Landmark, ShieldCheck, Hexagon, Zap, Square, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const CHAINS = [
    { icon: Hexagon, name: "Ethereum", agent: "agent:0x11c4…f2" },
    { icon: Zap, name: "Solana", agent: "agent:9xQe…v1" },
    { icon: Square, name: "Base", agent: "agent:0x88b2…9d" },
];

const card =
    "group relative overflow-hidden rounded-2xl border border-white/10 bg-[rgba(11,15,25,0.6)] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-neon-cyan/30 sm:p-8";

const exportAuditLog = () => {
    const sample = {
        exported_at: new Date().toISOString(),
        standard: ["SOC2", "GDPR", "MiCA"],
        entries: [
            { ts: "2025-11-04T12:04:11.482Z", suid: "suid:0x7f3e…a91c", action: "swap.exec", receipt: "0x9f2e…c4d7", result: "VERIFIED" },
            { ts: "2025-11-04T12:06:38.107Z", suid: "suid:0x7f3e…a91c", action: "oracle.fetch", receipt: "0x77aa…01be", result: "VERIFIED" },
        ],
    };
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "authena-audit-log.json";
    a.click();
    URL.revokeObjectURL(url);
};

export default function Pillars() {
    return (
        <section id="pillars" className="scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
            <div className="mx-auto max-w-7xl">
                <Reveal>
                    <Eyebrow>Core pillars</Eyebrow>
                    <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl">
                        One layer. Every proof that matters.
                    </h2>
                </Reveal>

                <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
                    <motion.div
                        data-testid="pillar-card-suid"
                        className={`${card} md:col-span-8`}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Fingerprint size={22} className="text-neon-cyan" />
                        <h3 className="mt-4 font-display text-lg font-semibold text-white sm:text-xl">
                            SUID — Super User Identity
                        </h3>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                            Every agent gets a canonical, chain-agnostic identity cryptographically bound to a
                            verified human principal. Agents act across protocols and chains while you keep the
                            kill-switch.
                        </p>

                        <div className="mt-8 flex flex-col items-stretch gap-4 rounded-xl border border-white/5 bg-black/30 p-5 sm:flex-row sm:items-center">
                            <div className="flex-1 space-y-3">
                                {CHAINS.map((c) => (
                                    <div key={c.name} className="flex items-center gap-3">
                                        <span className="inline-flex w-28 items-center gap-2 rounded-md border border-white/10 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-300">
                                            <c.icon size={12} className="text-neon-cyan" />
                                            {c.name}
                                        </span>
                                        <svg className="h-4 flex-1" preserveAspectRatio="none">
                                            <line
                                                x1="0"
                                                y1="8"
                                                x2="100%"
                                                y2="8"
                                                stroke="rgba(0,240,255,0.45)"
                                                strokeWidth="1.5"
                                                strokeDasharray="6 6"
                                                className="dash-flow"
                                            />
                                        </svg>
                                        <span className="hidden font-mono text-[9px] text-slate-500 sm:inline">
                                            {c.agent}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center gap-3 sm:ml-2 sm:flex-col sm:gap-2">
                                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-neon-emerald/40 bg-neon-emerald/10 glow-cyan">
                                    <Fingerprint size={22} className="text-neon-emerald" />
                                </span>
                                <span className="text-center font-mono text-[9px] uppercase tracking-[0.18em] text-neon-emerald">
                                    Verified
                                    <br />
                                    human
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        data-testid="pillar-card-proofs"
                        className={`${card} md:col-span-4`}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <ReceiptText size={22} className="text-neon-cyan" />
                        <h3 className="mt-4 font-display text-lg font-semibold text-white sm:text-xl">
                            Execution Proofs
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                            Cryptographic receipts for every agent action — API calls, prompts, swaps — proving
                            what actually happened, without exposing private context.
                        </p>
                        <div className="mt-6 rounded-xl border border-white/5 bg-black/40 p-4 font-mono text-[11px] leading-relaxed">
                            <p className="text-slate-500">RECEIPT #4821</p>
                            <p className="mt-1.5 text-slate-400">
                                action <span className="text-slate-200">api.call · 200 OK</span>
                            </p>
                            <p className="text-slate-400">
                                prompt <span className="text-slate-200">sha256:9b1d…e402</span>
                            </p>
                            <p className="text-slate-400">
                                time <span className="text-slate-200">12:04:11.482 UTC</span>
                            </p>
                            <p className="mt-2 inline-flex items-center gap-1.5 rounded border border-neon-emerald/40 px-1.5 text-[9px] uppercase tracking-[0.16em] text-neon-emerald">
                                <Check size={9} /> Verified · tamper-proof
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        data-testid="pillar-card-compliance"
                        className={`${card} md:col-span-4`}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <Landmark size={22} className="text-neon-cyan" />
                        <h3 className="mt-4 font-display text-lg font-semibold text-white sm:text-xl">
                            Compliance &amp; Auditability
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                            Immutable logs of agent actions, payments and coordination events — exportable in
                            one click for institutional review.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["SOC2 TYPE II", "GDPR", "MiCA"].map((b) => (
                                <span
                                    key={b}
                                    className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300"
                                >
                                    {b}
                                </span>
                            ))}
                        </div>
                        <button
                            data-testid="compliance-export-button"
                            onClick={exportAuditLog}
                            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-neon-cyan/30 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-neon-cyan transition-all duration-300 hover:bg-neon-cyan/10"
                        >
                            Download sample audit log
                        </button>
                    </motion.div>

                    <motion.div
                        data-testid="pillar-card-zk"
                        className={`${card} md:col-span-8`}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="grid items-center gap-8 sm:grid-cols-5">
                            <div className="sm:col-span-3">
                                <ShieldCheck size={22} className="text-neon-emerald" />
                                <h3 className="mt-4 font-display text-lg font-semibold text-white sm:text-xl">
                                    ZK Proof-of-Humanity
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                    Zero-knowledge humanity verification for dApps — stop Sybil agent attacks
                                    while biometric and identity data stay 100% private. No KYC leaks, ever.
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-neon-emerald/25 bg-neon-emerald/5 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neon-emerald">
                                    <ShieldCheck size={12} />
                                    Biometrics never leave the device
                                </div>
                            </div>
                            <div className="relative sm:col-span-2">
                                <div className="overflow-hidden rounded-xl border border-white/10">
                                    <img
                                        src="https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                        alt="Human hand reaching toward a glowing identity network"
                                        loading="lazy"
                                        className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[rgba(11,15,25,0.9)] via-transparent to-transparent" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}